import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { COUNTRIES, countryByName } from '@/lib/countries';

export default function CountrySelect({
  value,
  onChange,
  placeholder = 'Choose a country',
  disabled,
  testId,
  className,
}: {
  value: string | null | undefined;
  onChange: (name: string) => void;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const chosen = countryByName(value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal ${chosen ? '' : 'text-muted-foreground'} ${className ?? ''}`}
          data-testid={testId}
        >
          <span className="truncate">{chosen ? chosen.name : value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No country matches.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => { onChange(country.name); setOpen(false); }}
                  data-testid={testId ? `${testId}-option-${country.code}` : undefined}
                >
                  <Check className={`mr-2 h-4 w-4 ${chosen?.code === country.code ? 'opacity-100' : 'opacity-0'}`} />
                  {country.name}
                  <span className="ml-auto text-xs text-muted-foreground">{country.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
